import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState} from '../../../app/store';
import { 
  validateDkycRequestId,
  getAccessTokenForDkyc,
  getDigitalKycData
} from '../apis/DigitalKycApi';
import { GetDigitalKycDataResponse } from '../apis/models/GetDigitalKycDataResponse';

export interface DigitalKycState {
  loading: {
    validateDkycRequestId: 'idle' | 'loading' | 'failed';
    getAccessTokenForDkyc: 'idle' | 'loading' | 'failed';
    getDigitalKycData: 'idle' | 'loading' | 'failed';
  };
  hypervergeAccessToken: string | null;
  digitalKycData: GetDigitalKycDataResponse | null,
  doneActions: {
    validateDkycRequestId: boolean,
    getDigitalKycData: boolean
  },
  sourceOfDigitalKycData: "none" | "validateDkycRequestId" | "getDigitalKycData",
  error: any;
}

const initialState: DigitalKycState = {
  loading: {
    validateDkycRequestId: 'idle',
    getAccessTokenForDkyc: 'idle',
    getDigitalKycData: "idle"
  },
  hypervergeAccessToken: null,
  doneActions: {
    validateDkycRequestId: false,
    getDigitalKycData: false
  },
  digitalKycData: null,
  sourceOfDigitalKycData: "none",
  error : null,
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const validateDkycRequestIdAsync = createAsyncThunk(
  'digitalKyc/validateDkycRequestId',
  async (requestId: string) => {
    const response = await validateDkycRequestId(requestId);

    return response;
  }
);

export const getAccessTokenForDkycAsync = createAsyncThunk(
  'digitalKyc/getAccessTokenForDkyc',
  async (requestId: string) => {
    const response = await getAccessTokenForDkyc(requestId);

    return response;
  }
);

export const getDigitalKycDataAsync = createAsyncThunk(
  'digitalKyc/getDigitalKycData',
  async (requestId: string) => {
    const response = await getDigitalKycData(requestId);

    return response;
  }
);


export const digitalKycSlice = createSlice({
  name: 'videoKycRequestMeta',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setDigitalKyc:(state, action: PayloadAction<any>) => {
      state.digitalKycData = {
        ...state.digitalKycData,
        ...action.payload
      };
    },
    resetValidateDkycFlag: (state) => {
      state.doneActions.validateDkycRequestId = false;
    },
    resetgetDigitalKycDataFlag: (state) => {
      state.doneActions.getDigitalKycData = false;
    }
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(validateDkycRequestIdAsync.pending, (state) => {
        state.loading.validateDkycRequestId = 'loading';
      })
      .addCase(validateDkycRequestIdAsync.fulfilled, (state, action) => {
        state.loading.validateDkycRequestId = 'idle';
        state.doneActions.validateDkycRequestId = true;
        state.digitalKycData = {
          kycResult: null,
          requestDetails: action.payload.requestDetails
        };
        state.sourceOfDigitalKycData = "validateDkycRequestId"
      })
      
      .addCase(validateDkycRequestIdAsync.rejected, (state, action) => {
        state.loading.validateDkycRequestId = 'failed';
        state.error = action.error.message;
      })
      
      .addCase(getAccessTokenForDkycAsync.pending, (state) => {
        state.loading.getAccessTokenForDkyc = 'loading';
      })
      .addCase(getAccessTokenForDkycAsync.fulfilled, (state, action) => {
        state.loading.getAccessTokenForDkyc = 'idle';
        state.hypervergeAccessToken = action.payload;
      })
      .addCase(getAccessTokenForDkycAsync.rejected, (state, action) => {
        state.loading.getAccessTokenForDkyc = 'failed';
        state.error = action.error.message;
      }) 
      
      .addCase(getDigitalKycDataAsync.pending, (state) => {
        state.loading.getDigitalKycData = 'loading';
      })
      .addCase(getDigitalKycDataAsync.fulfilled, (state, action) => {
        state.loading.getDigitalKycData = 'idle';
        state.digitalKycData = action.payload;
        state.doneActions.getDigitalKycData = true;
        state.sourceOfDigitalKycData = "getDigitalKycData";
      })
      .addCase(getDigitalKycDataAsync.rejected, (state, action) => {
        state.loading.getDigitalKycData = 'failed';
        state.error = action.error.message;
      })  
  }
});

export const { setDigitalKyc, resetValidateDkycFlag, resetgetDigitalKycDataFlag } = digitalKycSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectDigitalKyc = (state: RootState) => state.digitalKyc;

export default digitalKycSlice.reducer;